import React from "react";
import { Box, Container, Text, Flex, VStack } from "@chakra-ui/react";
import { HashLink } from "react-router-hash-link";
import { motion } from "framer-motion";
import { Zap } from "lucide-react";

const MotionBox = motion(Box);

const freeItems = [
  "Clear, structured explanations",
  "Concept-linked learning",
  "Exam-relevant thinking",
];

export const Instructor = () => {
  return (
    <Box
      py={{ base: "64px", md: "96px" }}
      position="relative"
      overflow="hidden"
    >
      {/* Gradient background */}
      <Box
        position="absolute"
        inset={0}
        bgGradient="linear(135deg, #E9FFFF 0%, rgba(128,156,255,0.15) 50%, #E9FFFF 100%)"
        zIndex={0}
      />
      {/* Subtle circle */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%,-50%)"
        w="600px"
        h="600px"
        borderRadius="50%"
        border="80px solid rgba(0,149,200,0.06)"
        pointerEvents="none"
        zIndex={0}
      />

      <Container maxW="800px" textAlign="center" position="relative" zIndex={1}>
        <VStack spacing={8}>

          <MotionBox
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Box
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={4}
              py={2}
              borderRadius="full"
              bg="rgba(0,149,200,0.1)"
              border="1px solid rgba(0,149,200,0.25)"
              mb={2}
            >
              <Zap size={14} color="#0095C8" strokeWidth={2.5} />
              <Text
                fontFamily="'Inter', sans-serif"
                fontSize="0.8125rem"
                fontWeight="600"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="#0095C8"
              >
                Try it free
              </Text>
            </Box>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Text
              as="h2"
              fontFamily="'Public Sans', sans-serif"
              fontWeight="600"
              fontSize={{ base: "1.75rem", md: "2.375rem" }}
              color="#293452"
              lineHeight="1.25"
            >
              See the difference{" "}
              <Box as="span" color="#0095C8">for yourself</Box>
            </Text>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize={{ base: "0.9375rem", md: "1.0625rem" }}
              color="rgba(41,52,82,0.65)"
            >
              Start with free MCQs and experience:
            </Text>
          </MotionBox>

          {/* Feature pills */}
          <MotionBox
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            w="full"
          >
            <Flex
              gap={3}
              justify="center"
              flexWrap="wrap"
            >
              {freeItems.map((item, i) => (
                <Box
                  key={item}
                  px={5}
                  py={3}
                  borderRadius="full"
                  bg="white"
                  border="1.5px solid rgba(0,149,200,0.25)"
                  boxShadow="0 2px 8px rgba(0,149,200,0.08)"
                  _hover={{
                    borderColor: "#0095C8",
                    boxShadow: "0 4px 16px rgba(0,149,200,0.15)",
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.25s cubic-bezier(0.4,0,0.2,1)"
                  cursor="default"
                >
                  <Text
                    fontFamily="'Inter', sans-serif"
                    fontSize="0.9rem"
                    fontWeight="500"
                    color="#293452"
                    whiteSpace="nowrap"
                  >
                    ✦ {item}
                  </Text>
                </Box>
              ))}
            </Flex>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize="0.875rem"
              color="rgba(41,52,82,0.45)"
              fontStyle="italic"
            >
              No commitment required.
            </Text>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <HashLink smooth to="/#contact">
              <Box
                as="button"
                px="40px"
                py="16px"
                borderRadius="8px"
                background="linear-gradient(135deg, #0095C8, #016293)"
                color="white"
                fontFamily="'Public Sans', sans-serif"
                fontWeight="600"
                fontSize="1.0625rem"
                letterSpacing="0.02em"
                boxShadow="0 4px 20px rgba(0,149,200,0.35)"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 32px rgba(0,149,200,0.45)",
                }}
                _active={{ transform: "translateY(0)" }}
                transition="all 0.25s cubic-bezier(0.4,0,0.2,1)"
              >
                Start Free Practice
              </Box>
            </HashLink>
          </MotionBox>

        </VStack>
      </Container>
    </Box>
  );
};
